import React, {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function TimesheetDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [timesheet, setTimesheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(location.state?.saved || false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const now = new Date();
  const defaultDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [entryForm, setEntryForm] = useState({startAt: defaultDateTime, endAt: defaultDateTime, description: ''});
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteEntryId, setDeleteEntryId] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [clockedIn, setClockedIn] = useState(null);
  const [clocking, setClocking] = useState(false);
  const [userMap, setUserMap] = useState({});

  useEffect(() => { if (saved) { const t = setTimeout(() => setSaved(false), 3000); return () => clearTimeout(t); } }, [saved]);

  const load = () => {
    Promise.all([
      api(`/timesheets/timesheets/${id}`).then(r => r.ok ? r.json() : null),
      api(`/timesheets/timesheets/${id}/entries`).then(r => r.ok ? r.json() : []),
      api('/timesheets/clock/active-entries').then(r => r.ok ? r.json() : []),
    ]).then(([ts, ents, active]) => { setTimesheet(ts); setEntries(ents.sort((a, b) => b.date.localeCompare(a.date))); if (ts) setClockedIn(active.some(a => a.employeeId === ts.employeeId)); setLoading(false); });
  };

  useEffect(() => {
    load();
    api('/tenant-users').then(r => r.ok ? r.json() : []).then(list => {
      const map = {};
      list.forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`.trim() || u.username; });
      setUserMap(map);
    }).catch(() => {});
  }, [id]);

  const handleDeleteTimesheet = () => {
    api(`/timesheets/timesheets/${id}`, {method: 'DELETE'}).then(() => navigate('/timesheets'));
  };

  const toLocalDatetimeStr = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const handleAddEntry = (e) => {
    e.preventDefault();
    const payload = {startAt: new Date(entryForm.startAt).toISOString(), endAt: new Date(entryForm.endAt).toISOString(), description: entryForm.description};
    api(`/timesheets/timesheets/${id}/entries`, {method: 'POST', body: JSON.stringify(payload)})
      .then(() => { setShowAddEntry(false); setEntryForm({startAt: '', endAt: '', description: ''}); load(); });
  };

  const handleEditEntry = (e) => {
    e.preventDefault();
    const payload = {startAt: new Date(entryForm.startAt).toISOString(), endAt: new Date(entryForm.endAt).toISOString(), description: entryForm.description};
    api(`/timesheets/timesheets/${id}/entries/${editingEntry}`, {method: 'PUT', body: JSON.stringify(payload)})
      .then(() => { setEditingEntry(null); setEntryForm({startAt: '', endAt: '', description: ''}); load(); });
  };

  const startEditEntry = (entry) => {
    setEditingEntry(entry.id);
    setEntryForm({startAt: toLocalDatetimeStr(entry.startAt), endAt: toLocalDatetimeStr(entry.endAt), description: entry.description || ''});
  };

  const handleDeleteEntry = () => {
    api(`/timesheets/timesheets/${id}/entries/${deleteEntryId}`, {method: 'DELETE'})
      .then(() => { setDeleteEntryId(null); load(); });
  };

  const setEntry = (field) => (e) => setEntryForm(f => ({...f, [field]: e.target.value}));

  if (loading) return <Layout><div className="p-6">Loading...</div></Layout>;
  if (!timesheet) return <Layout><div className="p-6">Timesheet not found.</div></Layout>;

  const statusBadge = timesheet.status === 'APPROVED'
    ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">APPROVED</span>
    : <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">DRAFT</span>;

  return (
    <Layout>
      {saved && <div className="mb-4 px-4 py-3 bg-green-100 text-green-800 rounded-lg">Timesheet saved successfully.</div>}
      <Link to="/timesheets" className="text-teal-600 hover:text-teal-800 mb-4 inline-block">&larr; Back</Link>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900">{timesheet.employeeName}</h2>
            {clockedIn === false && (
              <button disabled={clocking} onClick={() => { setClocking(true); api(`/timesheets/clock/in/${timesheet.employeeId}`, {method: 'POST'}).then(() => { setClocking(false); load(); }); }}
                className="px-3 py-1 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                {clocking ? 'Clocking In...' : 'Clock In'}
              </button>
            )}
            {clockedIn === true && (
              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-lg font-medium">Clocked In</span>
            )}
          </div>
          <div className="flex gap-3">
            <Link to={`/timesheets/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
            <button onClick={() => setShowDelete(true)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Period</label>
            <p className="mt-1 text-gray-900">{timesheet.startDate} — {timesheet.endDate}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <p className="mt-1">{statusBadge}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Total Hours</label>
            <p className="mt-1 text-gray-900 font-semibold">{timesheet.totalHours != null ? Number(timesheet.totalHours).toFixed(2) : '—'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900">Entries ({entries.length})</h3>
          <button onClick={() => { setShowAddEntry(true); setEditingEntry(null); setEntryForm({startAt: defaultDateTime, endAt: defaultDateTime, description: ''}); }}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">Add Entry</button>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {entries.map(e => {
              const displayStart = e.startAt ? new Date(e.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
              const displayEnd = e.endAt ? new Date(e.endAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
              const hasClockDetails = e.entrySource === 'CLOCK' || e.clockInLatitude != null || e.clockInIpAddress;
              const isExpanded = expandedEntry === e.id;
              return (
              <React.Fragment key={e.id}>
              <tr className={`hover:bg-gray-50 ${hasClockDetails ? 'cursor-pointer' : ''}`}
                onClick={() => hasClockDetails && setExpandedEntry(isExpanded ? null : e.id)}>
                <td className="px-6 py-4 text-gray-900">{e.date}{e.endDate && e.endDate !== e.date ? ` — ${e.endDate}` : ''}</td>
                <td className="px-6 py-4 text-gray-600">{displayStart}</td>
                <td className="px-6 py-4 text-gray-600">{displayEnd}</td>
                <td className="px-6 py-4 text-gray-900 font-medium">{e.totalHours != null ? Number(e.totalHours).toFixed(2) : '—'}</td>
                <td className="px-6 py-4 text-gray-600">{e.description || '—'}</td>
                <td className="px-6 py-4" onClick={ev => ev.stopPropagation()}>
                  <button onClick={() => startEditEntry(e)} className="text-gray-600 hover:text-gray-900 mr-3 text-sm">Edit</button>
                  <button onClick={() => setDeleteEntryId(e.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                </td>
              </tr>
              {isExpanded && (
                <tr className="bg-gray-50">
                  <td colSpan={6} className="px-6 py-4">
                    <div className="grid grid-cols-2 gap-6 text-sm">
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Clock In</p>
                        {e.clockInLatitude != null && <p className="text-gray-500">📍 {e.clockInLatitude.toFixed(6)}, {e.clockInLongitude.toFixed(6)}</p>}
                        {e.clockInIpAddress && <p className="text-gray-500">🌐 {e.clockInIpAddress}</p>}
                        {e.clockInBy && <p className="text-gray-500">👤 {userMap[e.clockInBy] || e.clockInBy}</p>}
                      </div>
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Clock Out</p>
                        {e.clockOutLatitude != null && <p className="text-gray-500">📍 {e.clockOutLatitude.toFixed(6)}, {e.clockOutLongitude.toFixed(6)}</p>}
                        {e.clockOutIpAddress && <p className="text-gray-500">🌐 {e.clockOutIpAddress}</p>}
                        {e.clockOutBy && <p className="text-gray-500">👤 {userMap[e.clockOutBy] || e.clockOutBy}</p>}
                        {!e.endAt && <p className="text-gray-400 italic">Still clocked in</p>}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {entries.length === 0 && <div className="text-center py-12 text-gray-500">No entries yet.</div>}
      </div>

      {/* Add/Edit Entry Modal */}
      {(showAddEntry || editingEntry) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{editingEntry ? 'Edit Entry' : 'Add Entry'}</h3>
            <form onSubmit={editingEntry ? handleEditEntry : handleAddEntry} className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Start</label>
                  <button type="button" onClick={() => setEntryForm(f => ({...f, startAt: defaultDateTime}))}
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium">Now</button>
                </div>
                <input type="datetime-local" required value={entryForm.startAt} onChange={setEntry('startAt')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">End</label>
                  <button type="button" onClick={() => setEntryForm(f => ({...f, endAt: defaultDateTime}))}
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium">Now</button>
                </div>
                <input type="datetime-local" required value={entryForm.endAt} onChange={setEntry('endAt')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={entryForm.description} onChange={setEntry('description')} rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => { setShowAddEntry(false); setEditingEntry(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                  {editingEntry ? 'Save' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Timesheet Modal */}
      {showDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Timesheet</h3>
            <p className="text-gray-600 mb-6">This will delete the timesheet and all its entries.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDelete(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDeleteTimesheet} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Entry Modal */}
      {deleteEntryId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Entry</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this entry?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteEntryId(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDeleteEntry} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
